import { Box, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Panel, PanelGroup } from "react-resizable-panels";
import { AppContext } from "../App";
import { SideNavigationPage } from "../common/SideNavigationPage/SideNavigationPage";
import { GetCurrentUserInfoDocument, GetProjectDocument } from "../generated";

import { useQuery } from "@apollo/client";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReorderIcon from "@mui/icons-material/Reorder";
import { useParams } from "react-router-dom";
import chatApi from "../api/chatApi";
import { BrightBotSmileIcon } from "../common/Icons/BrightBotSmileIcon";
import { SideViewportIcon } from "../common/Icons/SideViewportIcon";
import { ChatBot } from "./components/ChatBot";
import { SessionNav } from "./components/SessionNav";
import {
  BrightbotIconContainer,
  ChatContainer,
  ContentContainer,
  CustomPanelResizeHandle,
  LeftHeaderContainer,
  RightHeaderContainer,
  SidebarIconContainer,
  StyledPanelResizerContainer,
} from "./style";

import { useSideBarStore } from "../store/useSideBarStore";
import { useSessionStore, useSessionActions } from "../store/useSessionStore";
import AssistantSelector from "./components/AssistantSelector";
import BrightbotStateSidebar from "./components/BrightbotStateSidebar";
import ChatbotSkeleton from "./Skeletons/ChatbotSkeleton";
import useResizeSidebar from "./hooks/useResizeSidebar";
import React from "react";
import { SessionErrorBoundary } from "../components/ErrorBoundaries";

type Props = {};

export const Brightbot = ({}: Props) => {
  const { projectId, workspaceId, threadId } = useParams();
  const [threads, setThreads] = useState();
  const { loading } = useSideBarStore((state) => state);

  // Use Zustand store for sidebar and refetch state
  const sidebar = useSessionStore.use.sidebar();
  const refetch = useSessionStore.use.refetch();
  const { setSidebar: setSidebarStore, setRefetch } = useSessionActions();

  // Create a wrapper for setSidebar that can handle React.SetStateAction<boolean>
  const setSidebar = React.useCallback(
    (value: React.SetStateAction<boolean>) => {
      if (typeof value === "function") {
        setSidebarStore(value(sidebar));
      } else {
        setSidebarStore(value);
      }
    },
    [sidebar, setSidebarStore]
  );

  // Session reset handler for error boundary
  const handleSessionReset = React.useCallback(() => {
    // Reset session state
    setRefetch(true);
    // Reset sidebar state
    setSidebarStore(false);
    // Clear any other session-related state if needed
  }, [setRefetch, setSidebarStore]);

  // handles the panel controls
  const { rightSidebarRef, handleSidebarDisplay, handleDragging } =
    useResizeSidebar({ sidebar, setSidebar });

  const { data: currentUser } = useQuery(GetCurrentUserInfoDocument);

  const { data } = useQuery(GetProjectDocument, {
    variables: {
      input: {
        workspaceId: workspaceId || "",
      },
      projectFilter: {
        projectId: projectId || "",
      },
    },
  });

  const workspace = data?.workspace;

  useEffect(() => {
    const abortController = new AbortController();
    if ((currentUser?.currentUser.id, workspaceId)) {
      chatApi
        .post(
          "/threads/search",
          {
            metadata: {
              user_id: currentUser?.currentUser.id,
              workspace_id: workspaceId,
            },
          },
          {
            signal: abortController.signal,
          }
        )
        .then((res) => {
          setThreads(res.data);
        });
    }

    return () => {
      abortController.abort();
    };
  }, [currentUser?.currentUser.id, workspaceId, refetch]);

  return (
    <AppContext.Consumer>
      {({ showSidenav, setShowSidenav }) => (
        <>
          <SideNavigationPage
            innerSideBar
            className={"full-width bg-white"}
            leftHeaderElements={
              <LeftHeaderContainer>
                <Tooltip title={showSidenav ? "Close sidebar" : "Open sidebar"}>
                  <SidebarIconContainer
                    onClick={() => setShowSidenav((prev) => !prev)}
                  >
                    {showSidenav ? <ArrowBackIcon /> : <ReorderIcon />}
                  </SidebarIconContainer>
                </Tooltip>

                <BrightbotIconContainer>
                  <BrightBotSmileIcon />
                </BrightbotIconContainer>

                <Typography variant="h5">BrightAgent</Typography>
              </LeftHeaderContainer>
            }
            rightContainerProps={{
              flex: "1",
            }}
            rightHeaderElements={
              <RightHeaderContainer>
                {threadId && (
                  <Box display="flex" alignItems="center" gap={5}>
                    {/* for debugging of agents */}
                    <AssistantSelector display={false} />
                    <Tooltip title={sidebar ? "Close sidebar" : "Open sidebar"}>
                      <Box onClick={handleSidebarDisplay}>
                        <SideViewportIcon sx={{ cursor: "pointer" }} />
                      </Box>
                    </Tooltip>
                  </Box>
                )}
              </RightHeaderContainer>
            }
            verticalNav={
              <SessionNav
                show={showSidenav}
                workspaceName={workspace?.name || ""}
                setShowSidenav={setShowSidenav}
                threads={threads}
                setRefetch={setRefetch}
              />
            }
            content={
              <ContentContainer>
                <SessionErrorBoundary onSessionReset={handleSessionReset}>
                  <PanelGroup direction="horizontal" style={{ height: "100%" }}>
                    <Panel style={{ height: "100%" }} minSize={30}>
                      <ChatContainer>
                        {!loading && threads ? (
                          <ChatBot />
                        ) : (
                          <ChatbotSkeleton />
                        )}
                      </ChatContainer>
                    </Panel>

                    <StyledPanelResizerContainer>
                      <CustomPanelResizeHandle
                        disabled={!sidebar}
                        onDragging={handleDragging}
                      />
                    </StyledPanelResizerContainer>
                    <Panel
                      ref={rightSidebarRef}
                      collapsible={!sidebar}
                      defaultSize={0}
                      minSize={sidebar ? 30 : 0}
                      style={{ height: "100%" }}
                    >
                      <BrightbotStateSidebar sidebar={sidebar} />
                    </Panel>
                  </PanelGroup>
                </SessionErrorBoundary>
              </ContentContainer>
            }
          />
        </>
      )}
    </AppContext.Consumer>
  );
};
